import {
  type InfiniteData,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query'
import { produce } from 'immer'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'
import { compareTagsByPosition } from '@/helpers/tags'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import { excerptsQueryKeys } from '@/hooks/excerpts/types'
import { organizationFilesQueryKeys } from '@/hooks/organization/types'
import { tagsQueryKeys } from '@/hooks/tags/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { PaginateRequest, PaginatedResponse } from '@/types/Api'
import type { File } from '@/types/File'
import type { UUID } from '@/types/Id'
import type { Excerpt } from '@/types/library/Excerpt'
import type { CreateTag, CreateTags, Tag, UpdateTag } from '@/types/Tag'

type TagUpdate = UpdateTag & Partial<Tag>

interface UpsertTagsRequest {
  tagsToUpdate: TagUpdate[]
  tagsToCreate: CreateTag[]
  tagsToDelete?: UUID[]
}

interface UseUpsertTagsArgs {
  orgId: UUID | ''
}

interface UpsertTagsMutationContext {
  previousTagsData?: InfiniteData<PaginatedResponse<Tag>, PaginateRequest>
}

interface MergeTagsQueryDataArgs {
  deletedTagIds: UUID[]
  nextTags: Tag[]
  oldData?: InfiniteData<PaginatedResponse<Tag>, PaginateRequest>
}

interface RemoveDeletedTagIdsArgs {
  deletedTagIds: Set<UUID>
  tagIds: UUID[]
}

const removeDeletedTagIds = ({
  deletedTagIds,
  tagIds,
}: RemoveDeletedTagIdsArgs): UUID[] => {
  if (deletedTagIds.size === 0) {
    return tagIds
  }

  return tagIds.filter((tagId) => !deletedTagIds.has(tagId))
}

const mergeTagsQueryData = ({
  deletedTagIds,
  nextTags,
  oldData,
}: MergeTagsQueryDataArgs): InfiniteData<
  PaginatedResponse<Tag>,
  PaginateRequest
> => {
  const tagsById = new Map<UUID, Tag>()

  if (oldData) {
    for (const page of oldData.pages) {
      for (const tag of page.data) {
        if (!deletedTagIds.includes(tag.id)) {
          tagsById.set(tag.id, tag)
        }
      }
    }
  }

  for (const tag of nextTags) {
    tagsById.set(tag.id, tag)
  }

  const mergedTags = Array.from(tagsById.values()).toSorted(
    compareTagsByPosition,
  )

  return {
    pageParams: oldData?.pageParams ?? [{ limit: 10000 }],
    pages: [
      {
        count: mergedTags.length,
        data: mergedTags,
        has_more: false,
      },
    ],
  }
}

const upsertTags = async (
  orgId: UUID | '',
  request: UpsertTagsRequest,
  accessToken: string,
): Promise<Tag[]> => {
  if (!orgId) {
    throw new Error('Org ID must be provided')
  }

  const results: Tag[] = []

  const hasUpdates = request.tagsToUpdate.length > 0
  const hasDeletes = (request.tagsToDelete?.length ?? 0) > 0
  if (hasUpdates || hasDeletes) {
    const req = {
      updates: request.tagsToUpdate,
      delete_ids: request.tagsToDelete ?? [],
    }
    const mutateRes = await fetch(
      `${API_URL}/api/v1/organizations/${orgId}/tags`,
      {
        method: 'PATCH',
        body: JSON.stringify(req),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    if (!mutateRes.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: `Failed to mutate tags: ${mutateRes.statusText}`,
        cause: {
          status: mutateRes.status,
        },
      })
    }

    const updatedTags = (await mutateRes.json()) as PaginatedResponse<Tag>
    results.push(...updatedTags.data)
  }

  if (request.tagsToCreate.length > 0) {
    const req: CreateTags = {
      tags: request.tagsToCreate,
    }
    const createRes = await fetch(
      `${API_URL}/api/v1/organizations/${orgId}/tags`,
      {
        method: 'POST',
        body: JSON.stringify(req),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )

    if (!createRes.ok) {
      throw new SweetspotError({
        name: 'FETCH_ERROR',
        message: `Failed to create tags: ${createRes.statusText}`,
        cause: {
          status: createRes.status,
        },
      })
    }

    const createdTags = (await createRes.json()) as PaginatedResponse<Tag>
    results.push(...createdTags.data)
  }

  return results
}

export const useUpsertTags = ({ orgId }: UseUpsertTagsArgs) => {
  const { accessToken } = useAuthInfoWithThrow()
  const { handleError } = useHandleError()

  const queryClient = useQueryClient()

  return useMutation<
    Tag[],
    Error,
    UpsertTagsRequest,
    UpsertTagsMutationContext
  >({
    mutationFn: (request) => upsertTags(orgId, request, accessToken),
    onMutate: async () => {
      const key = tagsQueryKeys.organization({ orgId })

      return {
        previousTagsData:
          queryClient.getQueryData<
            InfiniteData<PaginatedResponse<Tag>, PaginateRequest>
          >(key),
      }
    },
    onError: (err, variables, context) => {
      const key = tagsQueryKeys.organization({ orgId })
      queryClient.setQueryData<
        InfiniteData<PaginatedResponse<Tag>, PaginateRequest>
      >(key, context?.previousTagsData)
      handleError(err)
    },

    onSuccess: (data, variables) => {
      const deletedTagIds = variables.tagsToDelete ?? []
      const deletedTagIdSet = new Set(deletedTagIds)

      // update all excerpts with the new tags
      const excerptsQueryKey = excerptsQueryKeys.organization({ orgId })
      queryClient.setQueryData<PaginatedResponse<Excerpt>>(
        excerptsQueryKey,
        (oldData) => {
          if (!oldData) return oldData

          return produce(oldData, (draft) => {
            draft.data.forEach((excerpt) => {
              excerpt.tag_ids = removeDeletedTagIds({
                deletedTagIds: deletedTagIdSet,
                tagIds: excerpt.tag_ids,
              })
            })
          })
        },
      )

      // update all organization files with the new tags
      const organizationFilesQueryKey = organizationFilesQueryKeys.organization(
        { orgId },
      )
      queryClient.setQueryData<PaginatedResponse<File>>(
        organizationFilesQueryKey,
        (oldData) => {
          if (!oldData) return oldData

          return produce(oldData, (draft) => {
            draft.data.forEach((file) => {
              if (!file.organization_file) return

              file.organization_file.tag_ids = removeDeletedTagIds({
                deletedTagIds: deletedTagIdSet,
                tagIds: file.organization_file.tag_ids,
              })
            })
          })
        },
      )

      // update the tags query with the new tags
      const key = tagsQueryKeys.organization({ orgId })
      queryClient.setQueryData<
        InfiniteData<PaginatedResponse<Tag>, PaginateRequest>
      >(key, (oldData) =>
        mergeTagsQueryData({
          deletedTagIds,
          nextTags: data,
          oldData,
        }),
      )
      void queryClient.invalidateQueries({ queryKey: key })
    },
  })
}
